import React, { useEffect, useRef, useState } from 'react'

export type SlotGamePlay = { game_name?: string; identifier?: string }

const IFRAME_ALLOW = 'autoplay; fullscreen; clipboard-read; clipboard-write'

type Props = {
  gamePlay: SlotGamePlay
  gameUrl: string
  onClose: () => void
}

/**
 * Full-screen casino iframe overlay (iOS Safari playbook): delayed mount, loader, permissions.
 */
const SlotGameIframeOverlay = ({ gamePlay, gameUrl, onClose }: Props) => {
  const [showIframe, setShowIframe] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!gamePlay?.game_name) setShowIframe(false)
  }, [gamePlay?.game_name])

  useEffect(() => {
    if (!gameUrl) {
      setShowIframe(false)
      return
    }
    const t = window.setTimeout(() => setShowIframe(true), 120)
    return () => window.clearTimeout(t)
  }, [gameUrl])

  useEffect(() => {
    const refreshHeight = () => {
      const vh = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight
      document.documentElement.style.setProperty('--app-height', `${vh}px`)
    }
    refreshHeight()

    const onVVResize = () => refreshHeight()
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVVResize)
    }

    const preventScroll = (e: TouchEvent) => {
      if (overlayRef.current?.contains(e.target as Node)) {
        const iframe = overlayRef.current.querySelector('iframe')
        if (iframe && iframe.contains(e.target as Node)) return
      }
      e.preventDefault()
    }
    document.addEventListener('touchmove', preventScroll, { passive: false })

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onVVResize)
      }
      document.removeEventListener('touchmove', preventScroll)
    }
  }, [])

  return (
    <div className="slot-iframe show" ref={overlayRef}>
      <div className="slot-header">
        <div className="title">
          <h4 className="mb-0" style={{ fontSize: '20px', fontWeight: '400' }}>
            {gamePlay?.game_name}
          </h4>
        </div>
        <div className="close-slot-frame" onClick={onClose}>
          EXIT
        </div>
      </div>
      {gameUrl ? (
        showIframe ? (
          <iframe
            src={gameUrl}
            title={gamePlay?.game_name || 'Casino game'}
            allow={IFRAME_ALLOW}
            allowFullScreen
            style={{ width: '100%', border: 'none' }}
          />
        ) : (
          <div
            className="slot-iframe-loading"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div className="lds-hourglass" />
          </div>
        )
      ) : (
        <div
          className="slot-iframe-loading"
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div className="lds-hourglass" />
        </div>
      )}
    </div>
  )
}

export default SlotGameIframeOverlay
